import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { X } from 'lucide-preact';
import { cn } from '@/lib/cn';

export type TagInputState = 'default' | 'error';

export interface TagInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  max?: number;
  invalid?: boolean;
  state?: TagInputState;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Optional set of suggested tags shown as quick-add chips. */
  suggestions?: string[];
  validate?: (tag: string) => boolean;
  className?: string;
  'aria-label'?: string;
}

const SIZE: Record<NonNullable<TagInputProps['size']>, string> = {
  sm: 'min-h-7 px-1.5 py-0.5 text-xs',
  md: 'min-h-9 px-2 py-1 text-13',
  lg: 'min-h-10 px-2.5 py-1.5 text-sm',
};

const STATE: Record<TagInputState, string> = {
  default: 'border-border hover:border-border-strong focus-within:border-brand focus-within:shadow-focus',
  error: 'border-error hover:border-error focus-within:border-error focus-within:shadow-focus-error',
};

export function TagInput({
  value,
  onChange,
  placeholder = 'Añadir etiqueta…',
  max,
  invalid = false,
  state,
  disabled = false,
  size = 'md',
  suggestions,
  validate,
  className,
  'aria-label': ariaLabel,
}: TagInputProps) {
  const [draft, setDraft] = useState('');
  const resolvedState = invalid ? 'error' : state ?? 'default';
  const st = STATE[resolvedState];

  function add(raw: string) {
    const t = raw.trim();
    if (!t) return;
    if (value.includes(t)) return;
    if (validate && !validate(t)) return;
    if (max !== undefined && value.length >= max) return;
    onChange([...value, t]);
    setDraft('');
  }

  function remove(t: string) {
    onChange(value.filter((v) => v !== t));
  }

  function onKeyDown(e: JSX.TargetedKeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        aria-label={ariaLabel}
        className={cn(
          'flex min-w-0 w-full flex-wrap items-center gap-1 rounded-md border bg-surface text-fg transition-[border-color,box-shadow] duration-med ease-snappy',
          st,
          SIZE[size],
          disabled && 'opacity-60 pointer-events-none',
          className,
        )}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded bg-brand-tint px-1.5 py-0.5 text-2xs font-medium text-brand"
          >
            {tag}
            <button
              type="button"
              aria-label={`Quitar ${tag}`}
              onClick={() => remove(tag)}
              className="cursor-pointer rounded-sm hover:bg-brand-tint-strong"
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onInput={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={onKeyDown}
          onBlur={() => add(draft)}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          className="flex-1 min-w-[120px] border-0 bg-transparent outline-none placeholder:text-fg-faint"
        />
      </div>
      {suggestions && suggestions.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {suggestions
            .filter((s) => !value.includes(s))
            .slice(0, 8)
            .map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => add(s)}
                className="rounded-full border border-border-subtle bg-surface-2 px-2 py-0.5 text-2xs text-fg-muted hover:border-brand hover:bg-brand-tint hover:text-brand transition-colors duration-med"
              >
                + {s}
              </button>
            ))}
        </div>
      ) : null}
    </div>
  );
}
